// src/components/CommonChart.tsx
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import {
  AgChartOptions,
  AgCharts,
  AgChartCaptionOptions,
} from "ag-charts-community";

/** 차트 이미지를 외부(챗봇 복사/저장 등)에서 뽑기 위한 imperative 핸들. */
export interface SnChartHandle {
  /**
   * width/height를 주면 그 크기로 재렌더해 export한다.
   * (화면이 작아 범례가 화살표로 넘겨봐야 해도, 크게 뽑으면 그룹 전체가 이미지에 담긴다)
   */
  getImageDataURL: (opts?: {
    width?: number;
    height?: number;
  }) => Promise<string | null>;
}

interface SnChartProps {
  // title?: string;
  title?: string | AgChartCaptionOptions;
  type:
    | "custom"
    | "bar"
    | "line"
    | "pie"
    | "area"
    | "donut"
    | "scatter"
    | "radar"
    | "histogram";
  data: any[];
  xKey?: string | string[];
  yKey?: string | string[];
  yName?: string | string[];
  calloutLabelKey?: string;
  labelKey?: string;
  options?: Partial<AgChartOptions>;
  seriesOptions?: any;
  width?: number | string;
  height?: number | string;
  barWidth?: number;
  // series?: any[];
}

const SnChart = forwardRef<SnChartHandle, SnChartProps>(function SnChart(
  {
    title = "",
    type,
    data,
    xKey,
    yKey,
    yName,
    calloutLabelKey,
    labelKey,
    options = {},
    seriesOptions = {},
    width,
    height,
    // series,
  },
  ref
) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({
    getImageDataURL: async (opts?: { width?: number; height?: number }) => {
      // 1순위: ag-charts 인스턴스 API (지정 크기로 재렌더 export — 범례 전체 포함 가능)
      const instance = chartInstanceRef.current;
      if (instance && typeof instance.getImageDataURL === "function") {
        try {
          const dataUrl = await instance.getImageDataURL(opts);
          if (dataUrl) {
            return dataUrl;
          }
        } catch (e) {
          console.warn("SnChart getImageDataURL failed; falling back to canvas", e);
        }
      }
      // 2순위: 렌더된 캔버스에서 직접 추출 (버전/환경 무관 폴백)
      const canvas = chartRef.current?.querySelector("canvas");
      if (canvas) {
        try {
          return canvas.toDataURL("image/png");
        } catch (e) {
          console.warn("SnChart canvas.toDataURL failed", e);
        }
      }
      return null;
    },
  }));

  useEffect(() => {
    const baseSeries = { type } as any;
    let seriesArr = [];
    if (type === "custom" && seriesOptions.series) {
      seriesArr = seriesOptions.series;
    } else if (type === "bar" || type === "line") {
      if (Array.isArray(yKey)) {
        seriesArr = yKey.map((key, idx) => ({
          ...baseSeries,
          xKey,
          yKey: key,
          yName: Array.isArray(yName) ? yName[idx] : key,
          ...seriesOptions,
          fill: seriesOptions.fill ? seriesOptions.fill[idx] : undefined,
          stroke: seriesOptions.stroke ? seriesOptions.stroke[idx] : undefined,
          grouped: seriesOptions.grouped ?? true, // ✅ 추가
          groupPadding: seriesOptions.groupPadding ?? 0.3, // ✅ 추가
          barPadding: seriesOptions.barPadding ?? 0.2, // ✅ 추가
        }));
      } else {
        seriesArr = [
          {
            ...baseSeries,
            xKey,
            yKey,
            yName,
            ...seriesOptions,
            fill: Array.isArray(seriesOptions.fill)
              ? seriesOptions.fill[0]
              : seriesOptions.fill,
            stroke: Array.isArray(seriesOptions.stroke)
              ? seriesOptions.stroke[0]
              : seriesOptions.stroke,
            grouped: seriesOptions.grouped ?? true, // ✅ 추가
            groupPadding: seriesOptions.groupPadding ?? 0.3, // ✅ 추가
            barPadding: seriesOptions.barPadding ?? 0.2, // ✅ 추가
          },
        ];
      }
    } else if (type === "pie" || type === "donut") {
      const calloutLabelKeyToUse =
        seriesOptions.calloutLabelKey || calloutLabelKey || xKey;
      const labelKeyToUse = seriesOptions.labelKey || labelKey || xKey;
      if (Array.isArray(yKey)) {
        seriesArr = yKey.map((key, idx) => ({
          ...baseSeries,
          angleKey: key,
          calloutLabelKey: calloutLabelKeyToUse,
          labelKey: labelKeyToUse,
          label: seriesOptions.label,
          innerRadiusOffset: -20,
          yName: Array.isArray(yName) ? yName[idx] : key,
          ...seriesOptions,
          fill: seriesOptions.fill ? seriesOptions.fill[idx] : undefined,
          stroke: seriesOptions.stroke ? seriesOptions.stroke[idx] : undefined,
          calloutLabel: {
            enabled: false, // 콜아웃 레이블 표시 여부
          },
        }));
      } else {
        seriesArr = [
          {
            ...baseSeries,
            angleKey: yKey,
            calloutLabelKey: calloutLabelKeyToUse,
            labelKey: labelKeyToUse,
            label: seriesOptions.label,
            innerRadiusOffset: -20,
            yName,
            ...seriesOptions,
            fill: {
              type: "gradient",
              angle: 45,
              stops: [
                { offset: 0, color: "#2980b9" },
                { offset: 1, color: "#3a8dc9" },
              ],
            },
            stroke: Array.isArray(seriesOptions.stroke)
              ? seriesOptions.stroke[0]
              : seriesOptions.stroke,
            calloutLabel: {
              enabled: false, // 콜아웃 레이블 표시 여부
            },
          },
        ];
      }
    } else {
      // 기타 차트 타입 기본 처리 (line, area 등)
      seriesArr = [
        {
          ...baseSeries,
          xKey,
          yKey,
          yName,
          ...seriesOptions,
        },
      ];
    }
    const finalOptions: AgChartOptions = {
      ...(typeof title === "string" ? { title: { text: title } } : { title }),
      // title: { text: title },
      data,
      series: seriesArr,
      ...options,
      container: chartRef.current!,
    };

    if (chartInstanceRef.current) {
      try {
        chartInstanceRef.current.destroy();
      } catch (e) {
        console.warn("Chart destroy error ignored:", e);
      }
    }

    // 새로 생성
    chartInstanceRef.current = AgCharts.create(finalOptions);

    // 언마운트 시 파괴
    return () => {
      try {
        chartInstanceRef.current?.destroy();
      } catch (e) {
        console.warn("Chart destroy error ignored:", e);
      }
    };
  }, [
    title,
    type,
    data,
    xKey,
    yKey,
    yName,
    options,
    seriesOptions,
    calloutLabelKey,
    labelKey,
  ]);

  //   // 기존 차트가 있다면 파괴 후 새로 생성
  //   if (chartInstanceRef.current) {
  //     //   chartInstanceRef.current.destroy();
  //     try {
  //       chartInstanceRef.current.destroy();
  //     } catch (e) {
  //       console.warn("Chart destroy error ignored:", e);
  //     }
  //   }

  //   chartInstanceRef.current = AgCharts.create(finalOptions);

  //   return () => {
  //     chartInstanceRef.current?.destroy();
  //   };
  // }, [
  //   title,
  //   type,
  //   data,
  //   xKey,
  //   yKey,
  //   yName,
  //   options,
  //   seriesOptions,
  //   calloutLabelKey,
  //   labelKey,
  // ]);

  return (
    <div
      ref={chartRef}
      style={{ width: width ?? "100%", height: height ?? "100%" }}
    />
  );
});

export default SnChart;
